import { lazy, Suspense } from 'react';
import Layout from './components/layout/Layout';
import ToastContainer from './components/ui/ToastContainer';
import ErrorBoundary from './components/ui/ErrorBoundary';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import FaviconSync from './components/features/FaviconSync';
import { useAuth } from './hooks/useAuth';
import { profileCompletionRequiredForPath, profileContactRequired, storeProfileNext } from './lib/profileDelivery';
import { useI18n } from './hooks/useI18n';
import HomePage from './pages/HomePage';

const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const CompleteProfilePage = lazy(() => import('./pages/CompleteProfilePage'));
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const PwaInstallPage = lazy(() => import('./pages/PwaInstallPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const DemoPage = lazy(() => import('./pages/DemoPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function RouteFallback() {
  const { t } = useI18n();
  return <div className="flex min-h-[40vh] items-center justify-center" aria-busy="true"><p className="text-sm text-muted">{t('common_loading')}</p></div>;
}

function CompletionEnforcement() {
  const location = useLocation();
  const { user } = useAuth();
  if (user && profileContactRequired(user) && profileCompletionRequiredForPath(location.pathname)) {
    const allowed = ['/complete-profile', '/login', '/register'];
    const isAllowed = allowed.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
    if (!isAllowed) {
      storeProfileNext(location.pathname + location.search);
      return <Navigate to="/complete-profile" replace />;
    }
  }
  return null;
}

export default function App() {
  const isDemoHost = window.location.hostname === 'demo.tecnicelu.store';
  return (
    <ErrorBoundary>
      <FaviconSync />
      {isDemoHost ? (
        <>
          <ScrollToTop /><ToastContainer />
          <Suspense fallback={<RouteFallback />}><Routes>
            <Route path="*" element={<DemoPage />} />
          </Routes></Suspense>
        </>
      ) : (
      <Layout>
      <ScrollToTop /><ToastContainer />
      <CompletionEnforcement />
      <Suspense fallback={<RouteFallback />}><Routes><Route path="/" element={<HomePage />} /><Route path="/products" element={<ProductsPage />} /><Route path="/products/:slug" element={<ProductDetailPage />} /><Route path="/services" element={<ServicesPage />} /><Route path="/cart" element={<CartPage />} /><Route path="/checkout" element={<CheckoutPage />} /><Route path="/login" element={<LoginPage />} /><Route path="/register" element={<RegisterPage />} /><Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} /><Route path="/complete-profile" element={<ProtectedRoute><CompleteProfilePage /></ProtectedRoute>} /><Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} /><Route path="/pwa-install" element={<PwaInstallPage />} /><Route path="/privacy" element={<PrivacyPolicyPage />} /><Route path="/terms" element={<TermsPage />} /><Route path="/demo" element={<DemoPage />} /><Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/menu" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/notifications" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/newsletters" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/delivery" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/logo-strips" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/trust-badges" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/seo" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/interface" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/whatsapp" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/global" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/qr" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/receipts" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/brand-assets" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} /><Route path="/admin/promo-assets" element={<Navigate to="/admin/qr" replace />} /><Route path="*" element={<NotFoundPage />} />
      </Routes></Suspense>
      </Layout>
      )}
    </ErrorBoundary>
  );
}